import React, { useState, useEffect } from 'react';
import { X, MessageCircle, Send, Phone, Check, CheckCheck, Truck as TruckIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const initialMessages = [
  { id: 1, time: '10:32 AM', text: '🚚 *Load Match Alert!*\n\nMumbai → Delhi • 18 T Textiles\nRate: ₹85,000\nTruck: 32ft SXL\n\nReply YES to book.', type: 'system', delivered: true, read: true, category: 'load' },
  { id: 2, time: '10:35 AM', text: 'YES', type: 'user', delivered: true, read: true },
  { id: 3, time: '10:36 AM', text: '✅ *Load Booked Successfully!*\n\nLoad ID: LD9001\nShipper: Reliance Freight\nDriver contact will be shared once assigned.', type: 'system', delivered: true, read: true, category: 'booking' },
  { id: 4, time: '12:15 PM', text: '💰 *Payment Received*\n\n₹45,000 credited via UPI\nFrom: Kumar Traders\nRef: UPI/523698741\n\nAvailable balance: ₹1,48,500', type: 'system', delivered: true, read: true, category: 'payment' },
  { id: 5, time: '3:42 PM', text: '📍 *Trip Update*\n\nVehicle MH 12 AB 3456 crossed Nashik.\nCurrent progress: 65%\nETA Delhi: 8 hours', type: 'system', delivered: true, read: false, category: 'trip' },
  { id: 6, time: '5:10 PM', text: '📄 *e-Way Bill Generated*\n\nEWB #741852963012\nLoad LD9001\nValid till: 08 Jul 2025\n\nDownload from app.', type: 'system', delivered: true, read: false, category: 'document' },
];

const quickReplies = ['View My Loads', 'Post a Load', 'Check Payments', 'Track Shipment'];

export default function WhatsAppPanel({ open, onClose }) {
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');

  const send = (text) => {
    const msg = text || input;
    if (!msg.trim()) return;
    const now = new Date().toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
    const userMsg = { id: Date.now(), time: now, text: msg, type: 'user', delivered: true, read: false };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    // Simulate auto-reply
    setTimeout(() => {
      let reply = '✅ Got it! I’ll process your request and update you shortly.';
      if (/load/i.test(msg)) reply = '🚚 Showing your active loads:\n\n1. LD9001 Mumbai → Delhi (In Transit)\n2. LD9003 Ahmedabad → Pune (Loading)\n3. LD9004 Hyderabad → Kolkata (Assigned)';
      if (/payment/i.test(msg)) reply = '💰 Balance: ₹1,48,500\nPending: ₹65,000\nThis month: +₹4,28,500 received, ₹1,56,000 paid';
      if (/track/i.test(msg)) reply = '📍 LD9001 is 65% complete. Vehicle at Nashik. ETA Delhi: 8 hours.';
      const sysMsg = { id: Date.now() + 1, time: now, text: reply, type: 'system', delivered: true, read: false, category: 'reply' };
      setMessages(prev => [...prev, sysMsg]);
    }, 900);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:justify-end sm:pr-6 p-0 sm:p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white w-full sm:w-[400px] h-full sm:h-[620px] sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col">
        {/* WhatsApp Header */}
        <div className="bg-[#075E54] text-white p-3 flex items-center gap-3">
          <button onClick={onClose} className="sm:hidden"><X className="w-5 h-5" /></button>
          <div className="w-10 h-10 rounded-full bg-[#00A651] flex items-center justify-center"><TruckIcon className="w-5 h-5" /></div>
          <div className="flex-1">
            <p className="font-semibold text-sm">EZLOAD Assistant</p>
            <p className="text-[10px] text-white/80 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>online</p>
          </div>
          <button className="p-1.5 rounded hover:bg-white/10"><Phone className="w-4 h-4" /></button>
          <button onClick={onClose} className="hidden sm:block p-1.5 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>

        {/* Info banner */}
        <div className="bg-[#FFB800]/10 border-b border-[#FFB800]/20 px-3 py-2 text-[10px] text-[#8B5E00] text-center">
          Demo preview — real WhatsApp requires Twilio/Meta API keys
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ background: 'linear-gradient(rgba(229, 221, 213, 0.5), rgba(229, 221, 213, 0.5)), repeating-linear-gradient(45deg, #efeae2 0px, #efeae2 10px, #e8e2d8 10px, #e8e2d8 20px)' }}>
          {messages.map(m => (
            <div key={m.id} className={`flex ${m.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 shadow-sm ${m.type === 'user' ? 'bg-[#DCF8C6]' : 'bg-white'}`}>
                <p className="text-sm text-gray-800 whitespace-pre-line">{m.text}</p>
                <div className="flex items-center justify-end gap-1 mt-1">
                  <span className="text-[10px] text-gray-500">{m.time}</span>
                  {m.type === 'user' && (m.read ? <CheckCheck className="w-3 h-3 text-[#4FC3F7]" /> : <CheckCheck className="w-3 h-3 text-gray-400" />)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick replies */}
        <div className="px-3 py-2 bg-white border-t border-gray-100 flex gap-2 overflow-x-auto">
          {quickReplies.map(q => (
            <button key={q} onClick={() => send(q)} className="whitespace-nowrap text-xs bg-[#DCF8C6] text-[#075E54] px-3 py-1 rounded-full border border-[#075E54]/20 hover:bg-[#c4f0a0]">{q}</button>
          ))}
        </div>

        {/* Input */}
        <div className="p-2 bg-gray-100 flex items-center gap-2">
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder="Type a message" className="flex-1 h-10 rounded-full px-4 text-sm outline-none border-0" />
          <button onClick={() => send()} className="w-10 h-10 rounded-full bg-[#075E54] text-white flex items-center justify-center hover:bg-[#128C7E]"><Send className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
